import { ApiError, issueEnrollment, listMembershipPlans } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { MemberResponse, MembershipPlanResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// frequent-customer-memberships admin-UX fix (staging finding) — Capability 3
// "Enrollment is admin-initiated only" had a backend + api-client but no UI
// entry point. Mirrors ReassignVehicleDialog's self-contained-select pattern,
// but this dialog calls the api-client directly (instead of delegating to a
// parent onConfirm) because it needs the raw IssueEnrollmentResponse to
// decide what to render on success. The backend now ALSO returns
// `enrollmentUrl` (same link the mailer emailed) so the admin can copy it
// directly from this dialog — useful in staging where the `log` mailer only
// writes to server logs.

interface EnrollMemberDialogProps {
  member: MemberResponse | null;
  onCancel: () => void;
}

export function EnrollMemberDialog({ member, onCancel }: EnrollMemberDialogProps) {
  const { t } = useTranslation('memberships');
  const [plans, setPlans] = useState<MembershipPlanResponse[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planId, setPlanId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const [enrollmentUrl, setEnrollmentUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!member) {
      setPlanId('');
      setSubmitting(false);
      setError(null);
      setEnrolled(false);
      setEnrollmentUrl(null);
      setCopied(false);
      setPlans([]);
      return;
    }
    setLoadingPlans(true);
    setError(null);
    listMembershipPlans({ active: true })
      .then((res) => {
        setPlans(res.plans);
        setPlanId(res.plans[0]?.id ?? '');
      })
      .catch((e) => {
        setError(translateApiError(e, t, { fallback: t('enrollMemberDialog.loadPlansError') }));
      })
      .finally(() => setLoadingPlans(false));
  }, [member, t]);

  async function confirm() {
    if (!member || !planId) {
      setError(t('enrollMemberDialog.selectPlanRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await issueEnrollment(member.id, { planId });
      setEnrollmentUrl(result.enrollmentUrl);
      setEnrolled(true);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('enrollMemberDialog.enrollError') }));
      } else {
        setError(translateApiError(e, t, { fallback: t('enrollMemberDialog.genericError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    onCancel();
  }

  async function copyLink() {
    if (!enrollmentUrl) return;
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      setCopied(true);
    } catch {
      setError(t('enrollMemberDialog.copyError'));
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(next) => (!next ? close() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('enrollMemberDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('enrollMemberDialog.description', {
              name: member?.name ?? t('enrollMemberDialog.defaultMemberName'),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-4">
          {enrolled ? (
            <div className="space-y-3">
              <p
                role="status"
                className="rounded-sm border border-jq-success/40 bg-jq-success/10 px-3 py-2 text-[13px] text-jq-success"
              >
                {t('enrollMemberDialog.enrolledMessage', { name: member?.name })}
              </p>
              {enrollmentUrl && (
                <div>
                  <Label htmlFor="enrollment-url" className="mb-1.5 block text-[13px] font-medium">
                    {t('enrollMemberDialog.enrollmentLinkLabel')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="enrollment-url"
                      readOnly
                      value={enrollmentUrl}
                      onFocus={(e) => e.currentTarget.select()}
                      className="text-[13px]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={copyLink}
                      aria-label={t('enrollMemberDialog.copyLinkAriaLabel')}
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? t('enrollMemberDialog.copied') : t('enrollMemberDialog.copy')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label htmlFor="enroll-plan" className="mb-1.5 block text-[13px] font-medium">
                {t('enrollMemberDialog.planLabel')}
              </Label>
              <select
                id="enroll-plan"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                disabled={submitting || loadingPlans}
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {loadingPlans && <option value="">{t('enrollMemberDialog.loadingPlans')}</option>}
                {!loadingPlans && plans.length === 0 && (
                  <option value="">{t('enrollMemberDialog.noActivePlans')}</option>
                )}
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          {enrolled ? (
            <Button type="button" size="sm" onClick={close}>
              {t('enrollMemberDialog.done')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={close}
                disabled={submitting}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button type="button" size="sm" onClick={confirm} disabled={submitting || !planId}>
                {submitting ? t('enrollMemberDialog.enrolling') : t('enrollMemberDialog.enroll')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
